import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { Component, OnInit } from '@angular/core';
import { catchError, finalize, of } from 'rxjs';

import { EstateAgentDto, EstateAgentStatusDto, MetroDto, PropertySectorDto, SacplanRegistrationTypeDto, UrbanPlannerDto, UserDto } from '../../../../../app/services/property-matrixV2/models';
import { ConfigUserPermissionService } from '../../../../../app/services/property-matrixV2/custom-services/userPermission.service';
import { NotificationService } from '../../../../shared/services/notification-service/notification.service';
import { LookupService, UserManagementService } from '../../../../services/property-matrixV2/services';

@Component({
  selector: 'app-urban-planner-profile',
  templateUrl: './urban-planner-profile.component.html',
  styleUrls: ['./urban-planner-profile.component.scss', '../../../../../css/2-modules/_admin-portal.scss']
})
export class UrbanPlannerProfileComponent implements OnInit {

  loading: boolean = false;
  profileUrbanPlannerForm: FormGroup | null | undefined;
  professionUrbanPlannerForm: FormGroup | null | undefined;
  isAccountVerified: boolean = false;
  isAccountVerifiedDisabled: boolean = true;
  isContracted: boolean = false;
  isContractedDisabled: boolean = false;
  currentUser: UserDto | undefined;
  urbanPlannerDtoList: UrbanPlannerDto[] = [];
  metroList: MetroDto[] = [];
  sacplanRegistrationTypesList: SacplanRegistrationTypeDto[] = [];
  selectedSacplanRegistrationType: SacplanRegistrationTypeDto | undefined = { value: null };
  selectedMetro: MetroDto | undefined = { description: "None" };
  viewOnly: boolean = true;
  estateAgentList: EstateAgentDto[] = [];
  hasAdminRole: boolean = false;

  requiredRolesFull: string[] = [];
  requiredRolesView: string[] = [];
  currentUserRoles: string[] | null | undefined = [];
  passedInUserId: string | undefined;
  estateAgentForm: FormGroup | null | undefined;
  sectorList: PropertySectorDto[] = [];
  selectedSector: PropertySectorDto[] = [];
  estateAgentStatusesList: EstateAgentStatusDto[] = [];
  selectedStatus: EstateAgentStatusDto = {};

  constructor(
    private lookupService: LookupService,
    private userManagementService: UserManagementService,
    private notificationService: NotificationService,
    private _userPermissionService: ConfigUserPermissionService,
    private router: Router,
    private route: ActivatedRoute,
    private fb: FormBuilder,
  ) { }

  ngOnInit(): void {
    this.route.queryParams.subscribe(params => {
      this.passedInUserId = params['UserProfile'];
    });

    this.loadCurrentUserRole();
    this.getUserPermissionList();
    this.getMetro();
    this.fetchEstateAgentStatuses();
    this.fetchPropertySectors();
    this.prepareForms();
    if (this.passedInUserId) {
      this.setCurrentUser(this.passedInUserId);
    }
    else {
      this.getCurrentUser();
      this.getsacPlanRegistrationTypes();
    }
  }

  private prepareForms(): void {
    try {
      this.profileUrbanPlannerForm = this.fb.group({
        profileImage: [null],
        firstName: [""],
        surname: [""],
        landline: [null],
        mobileNr: [null],
        email: [null],
        identityNumber: [null]
      });

      this.professionUrbanPlannerForm = this.fb.group({
        graduatedAsPlanner: [null],
        institution: [null],
        yearsExperience: [null],
        SACPLANType: [null],
        SACPLANNumber: [null],
        metro: [null]
      });

      this.estateAgentForm = this.fb.group({
        sectors: [null],
        status: [null],
        yearsInOperation: [null],
        id: [null],
        isPrincipal: [null],
        principalUserId: [null],
        companyFidelityFundCertificateNumber: [null],
        individualReferenceNumber: [null],
        metro: [null],
      })
    }
    catch (e) {
      this.loading = false;
      this.notificationService.showErrorMessage("Error", "Could not save a user, please contact your administrator.");
    }
  }

  private async getUrbanPlannerDetails(): Promise<void> {
    this.loading = true;
    const plannerData = await this.lookupService.apiV1LookupGetUrbanPlannerWithUserIdGet({ UrbanPlannerUserId: this.currentUser?.id })
      .pipe(
        catchError((err) =>
          of(this.notificationService.showErrorMessage("Error", "Could not get the Network Planner data."))
        ),
        finalize(() => {
          this.loading = false;
        })
      )
      .subscribe((result: UrbanPlannerDto[] | void) => {
        if (result && result.length > 0) {
          this.urbanPlannerDtoList = [];
          this.urbanPlannerDtoList = result;
          this.selectedSacplanRegistrationType = this.urbanPlannerDtoList[0].sacplanRegistrationType;
          this.selectedMetro = this.currentUser?.metro;
          this.professionUrbanPlannerForm!.setValue({
            ["graduatedAsPlanner"]: this.urbanPlannerDtoList[0].information?.graduationYear,
            ["institution"]: this.urbanPlannerDtoList[0].information?.graduationInstitution, ["yearsExperience"]: this.urbanPlannerDtoList[0].information?.yearsOfExperience,
            ["SACPLANType"]: this.selectedSacplanRegistrationType, ["SACPLANNumber"]: this.urbanPlannerDtoList[0].fullSacplanRegistrationNumber,
            ["metro"]: this.selectedMetro
          })
        } else {
          this.notificationService.showWarningMessage("Warning", "No Network Planner data found.");
        }
      });
  }

  private async getEstateAgentDetails(): Promise<void> {
    this.loading = true;
    const PrincipleAgentData = await this.lookupService.apiV1LookupGetEstateUsersWithUserIdGet({ EstateAgentUserId: this.currentUser?.id })
      .pipe(
        catchError((err) =>
          of(this.notificationService.showErrorMessage("Error", "Could not get the Agent data."))
        ),
        finalize(() => {
          this.loading = false;
        })
      )
      .subscribe((result: EstateAgentDto[] | void) => {
        if (result && result.length > 0) {
          this.estateAgentList = [];
          this.estateAgentList = result;
          this.selectedMetro = this.currentUser?.metro;
          this.selectedStatus = this.estateAgentList[0].personalInformation?.status ?? null;
          this.estateAgentList[0].personalInformation?.sectors.forEach(sector => {
            this.selectedSector.push(sector);
          });
          this.estateAgentForm!.setValue({
            ["sectors"]: this.estateAgentList[0].personalInformation?.sectors ?? [],
            ["status"]: this.estateAgentList[0].personalInformation?.status ?? null,
            ["yearsInOperation"]: this.estateAgentList[0].personalInformation?.yearsInOperation ?? 0,
            ["id"]: this.estateAgentList[0].id ?? undefined,
            ["isPrincipal"]: this.estateAgentList[0].isPrincipal ?? undefined,
            ["principalUserId"]: this.estateAgentList[0].principalUserId ?? undefined,
            ["companyFidelityFundCertificateNumber"]: this.estateAgentList[0].companyInformation?.companyFidelityFundCertificateNumber ?? "",
            ["individualReferenceNumber"]: this.estateAgentList[0].companyInformation?.individualReferenceNumber ?? "",
            ["metro"]: this.selectedMetro ?? null
          })
        } else {
          this.notificationService.showWarningMessage("Warning", "No Agent data found.");
        }
      });
  }

  private validateAllFormFields(formGroup: FormGroup) {
    try {
      Object.keys(formGroup.controls).forEach(field => {
        const control = formGroup.get(field);
        if (control) {
          if (control instanceof FormGroup) {
            this.validateAllFormFields(control);
          } else {
            control.markAsTouched({ onlySelf: true });
          }
        }
      });
    }
    catch (e) {
      this.loading = false;
      this.notificationService.showErrorMessage("Error", "Could not save a user, please contact your administrator.");
    }
  }

  private async getCurrentUser(): Promise<void> {
    this.loading = true;
    const UserData = await this.userManagementService.apiV1UserManagementGetCurrentUserLoggedInUserGet()
      .pipe(
        catchError((err) =>
          of(this.notificationService.showWarningMessage('No Current User', 'No current user found.'))
        ),
        finalize(() => {
          this.loading = false;
        })
      )
      .subscribe((result: any | void) => {
        this.currentUser = result;
        this.profileUrbanPlannerForm!.setValue({ ["profileImage"]: null, ["firstName"]: this.currentUser?.name, ["surname"]: this.currentUser?.surname, ["landline"]: this.currentUser?.landLine, ["mobileNr"]: this.currentUser?.mobile, ["email"]: this.currentUser?.email, ["identityNumber"]: this.currentUser?.idNumber });
        this.isAccountVerified = false;
        if (this.currentUser?.userRoles?.some(ur => ur.roleName == "Network Agent" || ur.roleName == "Principal Agent" || ur.roleName == "Senior Agent")) {
          this.getEstateAgentDetails();
        } else if (this.currentUser?.userRoles?.some(ur => ur.roleName == "Network Planner")) {
          this.getUrbanPlannerDetails();
        } else {
          this.notificationService.showWarningMessage("Warning", "Below field will not be populated because the role is not agent or planner.");
        }
      });
  }

  public compareMetro(metro1: MetroDto, metro2: MetroDto) {
    return metro1 && metro2 ? metro1.id === metro2.id : metro1 === metro2;
  }

  public compareSacPlanRegistrationTypes(type1: SacplanRegistrationTypeDto, type2: SacplanRegistrationTypeDto) {
    return type1 && type2 ? type1.id === type2.id : type1 === type2;
  }

  private getMetro(): void {
    this.loading = true;
    this.lookupService.apiV1LookupGetAllMetroItemsGet()
      .pipe(
        catchError((err) =>
          of(this.notificationService.showErrorMessage("Error", "Could not get the Network Planner data."))
        ),
        finalize(() => {
          this.loading = false;
        })
      )
      .subscribe((metroList: MetroDto[] | void) => {
        if (metroList && metroList.length > 0) {
          this.metroList = [];
          this.metroList = metroList.sort((a, b) => a.description!.localeCompare(b.description!));
        }
      });
  }

  private getsacPlanRegistrationTypes(): void {
    this.loading = true;
    this.lookupService.apiV1LookupGetSacplanRegistrationTypesGet()
      .pipe(
        catchError((err) =>
          of(this.notificationService.showErrorMessage("Error", "Could not get the Network Planner data."))
        ),
        finalize(() => {
          this.loading = false;
        })
      )
      .subscribe((sacplanRegistrationTypeList: SacplanRegistrationTypeDto[] | void) => {
        if (sacplanRegistrationTypeList && sacplanRegistrationTypeList.length > 0) {
          this.sacplanRegistrationTypesList = [];
          this.sacplanRegistrationTypesList = sacplanRegistrationTypeList.sort((a, b) => a.value!.localeCompare(b.value!)
          );
        }
      });
  }

  public endorseUrbanplanner(): void {
    if (this.currentUser && this.currentUser.id) {
      let message = "";
      if (this.currentUser?.endorsed) {
        message = "Are you sure you no longer want to endorse this user?";
      } else {
        message = "Are you sure you want to endorse this user?";
      }
      this.notificationService.showConfirmMessage("Endorse user", message, "Yes", "No").then(confirmResult => {
        if (confirmResult) {
          this.loading = true;
          this.userManagementService.apiV1UserManagementEndorseUserPost({ body: this.currentUser })
            .pipe(
              catchError((err) =>
                of(this.notificationService.showErrorMessage("Error", "Could not endorse user."))
              ),
              finalize(() => {
                this.loading = false;
              })
            )
            .subscribe((result: boolean | void) => {
              if (result) {
                if (result == true) {
                  this.notificationService.showSuccessMessage("Saved", "Endorsed user completed.");
                  this.getCurrentUser();
                } else {
                  this.notificationService.showErrorMessage("Error", "Did not endorse user with errors found.");
                }
              }
            });
        }
      });
    } else {
      this.notificationService.showWarningMessage("Warning", "Error endorsing user.");
    }
  }

  private loadCurrentUserRole(): void {
    this.userManagementService.apiV1UserManagementGetUserTokenGet().subscribe(roleBasedAccessDto => {
      this.currentUserRoles = roleBasedAccessDto.roles;
      this.checkIfuserHasAdminRoles();
    });
  }

  private getUserPermissionList(): void {
    this._userPermissionService.getRequiredRoles().subscribe(data => {
      this.requiredRolesFull = data.requiredRolesFull;
      this.requiredRolesView = data.requiredRolesView;
    });
  }

  private getActionsForUser() {
    if (this.hasRequiredRoleFull()) {
      return ['view', 'edit'];
    } else if (this.hasRequiredRoleView()) {
      return ['view'];
    }
    return [];
  }

  private hasRequiredRoleFull(): boolean {
    return this.currentUserRoles?.some(role => this.requiredRolesFull.includes(role)) ?? false;
  }

  private hasRequiredRoleView(): boolean {

    return this.currentUserRoles?.some(role => this.requiredRolesView.includes(role)) ?? false;
  }

  private checkIfuserHasAdminRoles(): void {
    let roleCheck = this.hasRequiredRoleFull();
    this.hasAdminRole = roleCheck;
  }

  private async setCurrentUser(userid: string): Promise<void> {
    this.loading = true;
    const UserData = await this.userManagementService.apiV1UserManagementGetCurrentUserByIdGet({ id: userid })
      .pipe(
        catchError((err) =>
          of(this.notificationService.showWarningMessage('No Current User', 'No current user found.'))
        ),
        finalize(() => {
          this.loading = false;
        })
      )
      .subscribe((result: any | void) => {
        this.currentUser = result;
        this.profileUrbanPlannerForm!.setValue({ ["profileImage"]: null, ["firstName"]: this.currentUser?.name, ["surname"]: this.currentUser?.surname, ["landline"]: this.currentUser?.landLine, ["mobileNr"]: this.currentUser?.mobile, ["email"]: this.currentUser?.email, ["identityNumber"]: this.currentUser?.idNumber });
        this.isAccountVerified = false;
        if (this.currentUser?.userRoles?.some(ur => ur.roleName == "Network Agent" || ur.roleName == "Senior Agent" || ur.roleName == "Principal Agent")) {
          this.getEstateAgentDetails();
        } else if (this.currentUser?.userRoles?.some(ur => ur.roleName == "Network Planner")) {
          this.getUrbanPlannerDetails();
        } else {
          this.notificationService.showWarningMessage("Warning", "Below field will not be populated because the role is not agent or planner.");
        }
      });
  }

  private fetchEstateAgentStatuses(): void {
    this.lookupService.apiV1LookupGetEstateAgentStatusesGet().subscribe({
      next: (response: EstateAgentStatusDto[]) => {
        if (response && response.length > 0) {
          this.estateAgentStatusesList = response;
        } else {
          this.notificationService.showErrorMessage("Error", "Could not get Agent statuses.");
        }
      }
    });
  }

  private fetchPropertySectors(): void {
    this.lookupService.apiV1LookupGetPropertySectorsGet().subscribe({
      next: (response: PropertySectorDto[]) => {
        if (response && response.length > 0) {
          this.sectorList = response.sort((a, b) => a.intValue - b.intValue);
        } else {
          this.notificationService.showErrorMessage("Error", "Could not load property sector data.");
        }
      }
    });
  }

  public compareSector(sector1: PropertySectorDto, sector2: PropertySectorDto) {
    return sector1 && sector2 ? sector1.id === sector2.id : sector1 === sector2;
  }

  public compareStatus(status1: EstateAgentStatusDto, status2: EstateAgentStatusDto) {
    return status1 && status2 ? status1.id === status2.id : status1 === status2;
  }
}
